import { Patient } from '@/core/models/patient.model'
import { zodResolver } from '@hookform/resolvers/zod'
import _ from 'lodash'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { isPossiblePhoneNumber } from 'react-phone-number-input'
import { z } from 'zod'

const schema = z.object({
  secu: z.string(),
  name: z.string(),
  surname: z.string(),
  email: z.string().email(),
  birthday: z.date().max(new Date()).nullable(),
  gender: z.enum(['M', 'F', 'I']).optional(),
  phone: z.string().optional(),
  caisse: z.string().optional(),
  codeRegime: z.string().optional(),
  codeGestion: z.string().optional(),
  street: z.string().optional(),
  city: z.string().optional(),
  cp: z.string().optional(),
  country: z.string().optional(),
  weight: z.string().optional(),
  height: z.string().optional(),
  allergy: z.string().optional(),
  vaccins: z.string().optional(),
  notes: z.string().optional(),
  background: z.string().optional(),
  treatment: z.string().optional(),
  parents: z.array(
    z.object({
      firstname: z.string(),
      lastname: z.string(),
      phone: z.string().optional(),
      email: z.string().optional(),
      city: z.string().optional(),
      cp: z.string().optional(),
    })
  ),
  privateDoctor: z
    .object({
      firstname: z.string(),
      lastname: z.string(),
      phone: z.string().optional(),
      email: z.string().email(),
      city: z.string().optional(),
      cp: z.string().optional(),
    })
    .optional(),
  insured: z.string().optional(),
  insuredRelationship: z.string().optional(),
  insuranceProvider: z.string().optional(),
  primaryInsurance: z.string().optional(),
  secondaryInsurance: z.string().optional(),
  primaryPolicy: z.string().optional(),
  secondaryPolicy: z.string().optional(),
  primaryPharmacy: z.string().optional(),
})

export type PatientInfoFormData = z.infer<typeof schema>

export function usePatientInfoForm({ patient }: { patient: Patient }) {
  const initialValues = useMemo(() => getInitialValues(patient), [patient])

  const rhForm = useForm<PatientInfoFormData>({
    defaultValues: initialValues,
    resolver: zodResolver(schema),
  })

  return { rhForm }
}

function getInitialValues(patient: Patient): PatientInfoFormData {
  return {
    name: patient.userInfo.name,
    surname: patient.userInfo.surname,
    email: patient.email,
    gender: patient.userInfo.gender,
    phone:
      patient.contactInfo && typeof patient.contactInfo.phone === 'string' && isPossiblePhoneNumber(patient.contactInfo.phone) ? patient.contactInfo.phone : '',
    birthday: !patient?.userInfo.birthday ? null : new Date(patient.userInfo.birthday),
    secu: patient.secu,
    caisse: patient.caisse || '',
    codeRegime: patient.codeRegime || '',
    codeGestion: patient.codeGestion || '',
    notes: patient.notes || '',
    background: patient.background || '',
    treatment: patient.treatment || '',
    privateDoctor: patient.attendingPhysician
      ? {
          firstname: patient.attendingPhysician?.firstname || '',
          lastname: patient.attendingPhysician?.lastname || '',
          email: patient.attendingPhysician?.email || '',
          phone: patient.attendingPhysician?.phone || '',
          city: patient.attendingPhysician?.city || '',
          cp: _.toString(patient.attendingPhysician?.cp || ''),
        }
      : undefined,
    parents: _.map(patient?.parents, (parent) => ({
      firstname: parent.firstname,
      lastname: parent.lastname,
      email: parent.email || '',
      phone: parent.phone || '',
      city: parent.city || '',
      cp: (parent.cp || '') + '',
    })),
  }
}
